import React, { useEffect, useState } from 'react';
import { FlaskConicalIcon, XIcon } from 'lucide-react';
import { PROTOTYPE_GROUPS, PROTOTYPE_STATES } from '../../data/prototypeStates';
import type { PrototypeTarget } from '../../data/prototypeStates';
interface PrototypeStatesPanelProps {
  onGoTo: (target: PrototypeTarget) => void;
}

/**
 * A reviewer's index of every state the fixtures demonstrate. Scaffolding, so
 * it sits outside the product's visual language: dashed, monospaced labels,
 * and always reachable without navigating the app.
 */
export function PrototypeStatesPanel({
  onGoTo
}: PrototypeStatesPanelProps) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  return <>
      <button type="button" onClick={() => setOpen(true)} className="fixed bottom-4 right-4 z-40 inline-flex min-h-tap items-center gap-2 rounded-pill border border-dashed border-ink bg-card px-4 font-mono text-eyebrow-caption uppercase tracking-[0.1em] text-ink shadow-card hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-368" data-mp="mxn0f1dvxglf">
        <FlaskConicalIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-369" data-mp="2ro1xt1p9ycju" />
        Prototype states
      </button>

      {open ? <div className="fixed inset-0 z-50 flex justify-end" data-id="element-370" data-mp="3k9useo0f2kvj">
          <div aria-hidden="true" onClick={() => setOpen(false)} className="absolute inset-0 bg-ink" style={{
        opacity: 0.4
      }} data-id="element-371" data-mp="3kch6z7lq60n9" />

          <div role="dialog" aria-modal="true" aria-labelledby="prototype-states-title" className="relative flex h-full w-full max-w-md flex-col border-l border-dashed border-ink bg-card" data-id="element-372" data-mp="3akb8rg4vqmzm">
            <div className="flex items-start justify-between gap-3 border-b border-dashed border-rule px-5 py-4" data-id="element-373" data-mp="21b6nlvfco2dh">
              <div className="flex flex-col gap-1" data-id="element-374" data-mp="25c8la7tmc6dc">
                <h2 id="prototype-states-title" className="font-mono text-eyebrow-caption uppercase tracking-[0.1em] text-ink" data-id="element-375" data-mp="25f4kdzh7d13d">
                  Prototype states
                </h2>
                <p className="text-small text-ink-muted" data-id="element-376" data-mp="1hnfx4xj8n2ta">
                  Fixtures that demonstrate a state you would otherwise have to go looking for.
                  None of this panel is part of the product.
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-pill text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-377" data-mp="18pdr23kndjz5">
                <span className="sr-only" data-id="element-378" data-mp="3i4vanpesiksk">Close</span>
                <XIcon aria-hidden="true" className="h-4 w-4" data-id="element-379" data-mp="3d58w9cn86yi6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-380" data-mp="3qw5d667shr9e">
              {PROTOTYPE_GROUPS.map(group => {
            const states = PROTOTYPE_STATES.filter(state => state.group === group);
            if (states.length === 0) return null;
            return <section key={group} className="mb-6" data-id="element-381" data-mp="3vvgvhjnt14t3">
                    <h3 className="font-mono text-eyebrow-caption uppercase tracking-[0.1em] text-ink-muted" data-id="element-382" data-mp="3vuypvwf95e8i">
                      {group}
                    </h3>
                    <ul className="mt-2 flex flex-col gap-2" data-id="element-383" data-mp="9nqrxf6nw9mi">
                      {states.map(state => <li key={state.id} data-id="element-384" data-mp="2fggqzh2d0w9n">
                          <button type="button" onClick={() => {
                    onGoTo(state.target);
                    setOpen(false);
                  }} className="flex w-full flex-col gap-1 rounded-field border border-dashed border-rule px-4 py-3 text-left hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-385" data-mp="2fjb931xwi4zw">
                            <span className="text-body font-medium text-ink" data-id="element-386" data-mp="1u6krul62mi0n">{state.label}</span>
                            <span className="text-small text-ink-muted" data-id="element-387" data-mp="1y7mr31zzmu2m">{state.note}</span>
                          </button>
                        </li>)}
                    </ul>
                  </section>;
          })}
            </div>
          </div>
        </div> : null}
    </>;
}